<template>
    <div class="container">
        <div class="row mb-4 border-bottom">
            <div class="h2">{{ user.id ? 'Edit' : 'Add' }} User</div>
            <div class="btn-group ml-auto mb-2" role="toolbar" aria-label="Toolbar">
                <button class="btn btn-primary" title="Save"><i class="fas fa-save" @click="save()"></i></button>
                <a :href="href" class="btn btn-primary" title="User List"><i class="fas fa-list fa-lg"></i></a>
            </div>
        </div>       
        <div class="row justify-content-center">
            <div class="container">            

                <div class="form-group row">                
                    <div class="col-md-6">
                        <label for="first_name">First Name</label>
                        <input id="first_name" type="text" :class="['form-control', {'is-invalid': hasError('first_name')}]" v-model="firstName" required autofocus>
                        <span v-if="hasError('first_name')" class="invalid-feedback" role="alert">
                            <strong v-text="errorText('first_name')"></strong>
                        </span>
                    </div>

                    <div class="col-md-6">
                        <label for="last_name">Last Name</label>
                        <input id="last_name" type="text" :class="['form-control', {'is-invalid': hasError('last_name')}]" v-model="lastName" required>
                        <span v-if="hasError('last_name')" class="invalid-feedback" role="alert">
                            <strong v-text="errorText('last_name')"></strong>
                        </span>
                    </div>              
                </div> 

                <div class="form-group row">
                    <div class="col-md-6">
                        <label for="last_name">E-Mail Address</label>
                        <input id="email" type="email" :class="['form-control', {'is-invalid': hasError('email')}]" v-model="email" required>
                        <span v-if="hasError('email')" class="invalid-feedback" role="alert">
                            <strong v-text="errorText('email')"></strong>
                        </span>
                    </div>

                    <div class="col-md-6">
                        <label for="mobile_phone">Mobile Phone</label>
                        <telephone-input id="mobile_phone" :class="['form-control', {'is-invalid': hasError('mobile_phone')}]" v-model="mobilePhone" required></telephone-input>
                        <span v-if="hasError('mobile_phone')" class="invalid-feedback" role="alert">
                            <strong v-text="errorText('mobile_phone')"></strong>
                        </span>
                    </div>
                </div>

                <div class="form-group row">
                    <div class="col-sm-2">
                        <label for="items_per_page">Items Per Page</label>
                        <input id="items_per_page" type="number" :class="['form-control', {'is-invalid': hasError('mobile_phone')}]" min="5" step="5" v-model="itemsPerPage" required>
                        <span v-if="hasError('items_per_page')" class="invalid-feedback" role="alert">
                            <strong v-text="errorText('items_per_page')"></strong>
                        </span>                      
                    </div>
                </div>

                <div v-if="user && user.id && selectedRoles.includes(Role.PROVIDER)" class="row">
                    <div class="form-group col-md-4">
                        <signature href="/admin/users/upload/signature" :id="user.id" :value="signature"></signature>
                    </div>
                </div>                             

                <div class="form-group row">
                    <div class="col-md-6">
                        <label for="role">Role(s) <small class="text-muted" v-if="!user.is_su_admin"><em>(Ctrl+click to select multiple)</em></small></label>
                        <div v-if="user.is_su_admin" class="h4">Administrator</div>
                        <div v-else>
                            <select class="custom-select" v-model="selectedRoles" multiple :size="filteredRoles.length">
                                <option v-for="role in filteredRoles" :key="'user-role-' + role.id" :value="role.id">{{ role.name }}</option>
                            </select>
                        </div>                          
                    </div>
                </div>

                <div v-if="!user.is_su_admin" class="form-group row">
                    <div class="col">
                        <div class="custom-control custom-checkbox">
                            <input class="custom-control-input" type="checkbox" id="is_disabled" name="is_disabled" :checked="user.disabled_at" v-model="isDisabled">
                            <label class="custom-control-label" for="is_disabled">Disable Account</label>
                        </div>
                        <div v-if="user.disabled_at" class="text-muted font-italic" >
                            This account was disabled {{ user.disabled_at | localizeTime('YYYY-MM-DD HH:mm:ss') }} by {{ user.disabled_by.full_name }}.
                        </div>                          
                    </div>
                </div>

                <div v-if="!user.is_su_admin" class="form-group row">
                    <div class="col">
                        <label for="customer">Customer</label>
                        <div v-if="user.customer" id="customer" class="h4">{{ user.customer.name }}</div>
                        <div v-else>
                            <select id="customer" :class="['custom-select', {'is-invalid': hasError('customer_id')}]" v-model="customer" :disabled="!selectedRoles.length">
                                <option v-for="customer in filteredCustomers" :key="'user-customer-' + customer.id" :value="customer">{{ customer.name }}</option>
                            </select>
                            <span v-if="hasError('customer_id')" class="invalid-feedback" role="alert">
                                <strong v-text="errorText('customer_id')"></strong>
                            </span>                            
                        </div>
                    </div>
                </div>                

                <div v-if="!user.is_su_admin" class="form-group row">               
                    <div class="col-md-6">
                        <label for="clients>">Clients <small class="text-muted"><em>(Ctrl+click to select multiple)</em></small></label>
                        <select id="clients" class="custom-select" v-model="selectedClients" multiple :size="filteredClients.length" :disabled="!selectedRoles.length">
                            <option v-for="client in filteredClients" :key="'user-clients-' + client.id" :value="client.id">{{ client.name }}</option>
                        </select>
                    </div>                                               
                </div>

            </div>
        </div>
    </div>
    
</template>

<script>
import { CustomerType, Role } from '../../constants.js';

export default {
    props: {
        user: Object,
        roles: Array,
        customers: Array,
        clients: Array,
        signature: String,
        href: String
    },
    data() {
        return {
            firstName: null,
            lastName: null,
            email: null,
            mobilePhone: null,
            itemsPerPage: 15,
            selectedRoles: [],
            isDisabled: false,
            customer: null,
            selectedClients: [],
            errors: [],

            CustomerType: CustomerType,
            Role: Role
        }
    },
    computed: {
        filteredRoles: function () {
            var self = this;
            var retval = self.roles;

            if (self.customer) {
                retval = self.roles.filter(r => r.customer_types.some(c => c.id == self.customer.customer_type_id));
            }

            return retval;
        },
        filteredCustomers: function () {
            var self = this;
            return self.customers.filter(x => x.customer_type.roles.some(e => self.selectedRoles.includes(e.id)));
        },
        filteredClients: function () {
            var self = this;
            var retval = [];

            if (this.customer) {
                retval = (this.customer.customer_type_id == CustomerType.PROVIDER_CUSTOMER) ? 
                    this.clients.filter(e => e.customer_id == self.customer.id) : this.clients;
            }

            return retval;
        }
    },    
    methods: {
        save: function () {

            let href = '/ajax/admin/users';

            if (this.user && this.user.id) { 
                href += ('/' + this.user.id); 
            }

            axios.post(href, {
                'first_name': this.firstName,
                'last_name': this.lastName,
                'email': this.email,
                'mobile_phone': this.mobilePhone,
                'items_per_page': this.itemsPerPage,
                'selected_roles': this.selectedRoles,
                'is_disabled': this.isDisabled,
                'customer_id': this.customer ? this.customer.id : null,
                'selected_clients': this.selectedClients,
            })
            .then(response => {

                if (response.data.errors) {
                    this.errors = response.data.errors;
                }
                else if (response.data.toast) {
                    const toast = response.data.toast;
                     toastr[toast.level](toast.message, toast.title, {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                }
                else {
                    window.location.href = this.href;
                }
            })
            .catch(error => {
                console.log(error.response.data);
            });
        },
        hasError: function (name) {
            return this.errors.hasOwnProperty(name);
        },
        errorText: function (name) {
            return this.errors[name][0];
        }
    },
    created() {

        if (this.customers.length === 1) {
            this.customer = this.customers[0];
        }

        if (this.user) {
            this.firstName = this.user.first_name;
            this.lastName = this.user.last_name;
            this.email = this.user.email;
            this.mobilePhone = this.user.mobile_phone;
            this.itemsPerPage = this.user.items_per_page ? this.user.items_per_page : 15;
            this.selectedRoles = (this.user.roles && this.user.roles.length) ? this.user.roles.map(e => e.id) : [];
            this.isDisabled = this.user.is_disabled;
            this.customer = this.user.customer;
            this.selectedClients = (this.user.clients && this.user.clients.length) ? this.user.clients.map(e => e.id) : [];
        }     
    }  
}
</script>

<style scoped>

</style>